/**
 * Date and time formatting utilities
 */

/**
 * Converts 24-hour time format to 12-hour format with AM/PM
 * @param time - Time string in 24-hour format (e.g., "14:30", "09:00")
 * @returns Time string in 12-hour format (e.g., "2:30 pm", "9:00 am")
 *
 * @example
 * ```ts
 * format24to12Time("14:30") // Returns "2:30 pm"
 * format24to12Time("09:00") // Returns "9:00 am"
 * format24to12Time("00:00") // Returns "12:00 am"
 * format24to12Time("12:00") // Returns "12:00 pm"
 * ```
 */
export function format24to12Time(time: string): string {
  const [hour, minute] = time.split(':');
  const hourNum = Number.parseInt(hour, 10);
  const hours12 = hourNum % 12 || 12;
  const ampm = hourNum >= 12 ? 'pm' : 'am';
  return `${hours12}:${minute} ${ampm}`;
}

/**
 * Formats a date to "MMM DD (Day)" format
 * @param date - Date object or date string
 * @returns Formatted date string (e.g., "Mar 15 (Sun)")
 *
 * @example
 * ```ts
 * formatBatchStartDate(new Date("2025-03-15")) // Returns "Mar 15 (Sun)"
 * ```
 */
export function formatBatchStartDate(date: Date | string): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const day = dateObj.toLocaleString('en-US', { weekday: 'short' });
  const formattedDate = dateObj.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
  });
  return `${formattedDate} (${day})`;
}

/**
 * Gets the ordinal suffix for a number (st, nd, rd, th)
 * @param n - The number to get the suffix for
 * @returns The ordinal suffix (e.g., "st", "nd", "rd", "th")
 *
 * @example
 * ```ts
 * getOrdinalSuffix(1) // Returns "st"
 * getOrdinalSuffix(2) // Returns "nd"
 * getOrdinalSuffix(3) // Returns "rd"
 * getOrdinalSuffix(4) // Returns "th"
 * getOrdinalSuffix(11) // Returns "th"
 * getOrdinalSuffix(21) // Returns "st"
 * ```
 */
function getOrdinalSuffix(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return s[(v - 20) % 10] || s[v] || s[0];
}

/**
 * Formats a timestamp to "DDth Month, YYYY" format
 * @param timestamp - Epoch timestamp in seconds (as string or number)
 * @returns Formatted date string (e.g., "11th November, 2025")
 *
 * @example
 * ```ts
 * formatTimestampToReadableDate("1699704000") // Returns "11th November, 2023"
 * formatTimestampToReadableDate(1699704000) // Returns "11th November, 2023"
 * ```
 */
export function formatTimestampToReadableDate(
  timestamp: string | number
): string {
  const timestampNum =
    typeof timestamp === 'string' ? Number.parseInt(timestamp, 10) : timestamp;

  // Convert seconds to milliseconds for JavaScript Date
  const date = new Date(timestampNum * 1000);

  const day = date.getDate();
  const month = date.toLocaleString('en-US', { month: 'long' });
  const year = date.getFullYear();

  // Get ordinal suffix (st, nd, rd, th)
  const ordinalSuffix = getOrdinalSuffix(day);

  return `${day}${ordinalSuffix} ${month}, ${year}`;
}
